import {
  type ChangeEvent,
  type CompositionEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import type { FingerspellingEntry } from "../lib/fingerspelling";
import { isCorrectAnswer } from "../lib/fingerspelling-drill";

/** 不一致の入力が、この時間止まったら不正解にする */
export const ANSWER_IDLE_MS = 700;

/**
 * 確定のとき、iOS は値を一瞬だけ縮めてすぐ元に戻す（実機のログでは約2ms）。
 * 縮んだあと、この時間内に元の長さ以上に戻ったら、縮みは無かったことにする。
 */
export const SHRINK_RESTORE_WINDOW_MS = 100;

export type AnswerJudgeResult = "correct" | "incorrect";

type UseAnswerJudgeOptions = {
  entry: FingerspellingEntry;
  onJudge: (result: AnswerJudgeResult) => void;
  idleMs?: number;
};

/**
 * 「よむ」の入力と判定のタイミング。
 *
 * iPhone 実機で、確定前に入力欄の値を空にすると以降の入力が表示されなくなることを
 * 確認したため、入力欄の値は書き換えない。問題ごとに、始まりの時点の値の長さを基準にして、
 * それより後ろの文字を、その問題の答えとして判定する。
 */
export const useAnswerJudge = ({
  entry,
  onJudge,
  idleMs = ANSWER_IDLE_MS,
}: UseAnswerJudgeOptions) => {
  const [value, setValue] = useState("");
  const [baseline, setBaseline] = useState(0);

  const valueRef = useRef("");
  const baselineRef = useRef(0);
  const shrinkRef = useRef<{ baseline: number; at: number } | null>(null);
  const judgedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const entryRef = useRef(entry);
  const onJudgeRef = useRef(onJudge);
  entryRef.current = entry;
  onJudgeRef.current = onJudge;

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => clearTimer, [clearTimer]);

  const judge = useCallback(
    (result: AnswerJudgeResult) => {
      if (judgedRef.current) return;
      judgedRef.current = true;
      clearTimer();
      onJudgeRef.current(result);
    },
    [clearTimer],
  );

  /** final = 確定された（変換の確定・Enter）。不一致なら待たずに不正解にする */
  const evaluate = useCallback(
    (final: boolean) => {
      if (judgedRef.current) return;
      clearTimer();
      const answer = valueRef.current.slice(baselineRef.current);
      if (answer === "") return;
      if (isCorrectAnswer(answer, entryRef.current)) {
        judge("correct");
      } else if (final) {
        judge("incorrect");
      } else {
        timerRef.current = setTimeout(() => judge("incorrect"), idleMs);
      }
    },
    [clearTimer, judge, idleMs],
  );

  const setBaselineTo = useCallback((next: number) => {
    baselineRef.current = next;
    setBaseline(next);
  }, []);

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    valueRef.current = next;
    setValue(next);

    const now = Date.now();
    const shrink = shrinkRef.current;
    const recentShrink =
      shrink && now - shrink.at <= SHRINK_RESTORE_WINDOW_MS ? shrink : null;
    if (next.length < baselineRef.current) {
      // 値が基準より縮んだ。前の文字を消した・候補で置き換えた場合は、基準を下げて、
      // 以降の入力を答えにする。確定時の一瞬の縮みなら、すぐ戻るので、元の基準を覚えておく
      shrinkRef.current = {
        baseline: recentShrink?.baseline ?? baselineRef.current,
        at: now,
      };
      setBaselineTo(next.length);
    } else if (shrink) {
      shrinkRef.current = null;
      if (recentShrink && next.length >= recentShrink.baseline) {
        setBaselineTo(recentShrink.baseline);
      }
    }
    evaluate(false);
  };

  const onCompositionEnd = (_event: CompositionEvent<HTMLInputElement>) => {
    evaluate(true);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") evaluate(true);
  };

  /** 次の問題へ。値は変えず、いまの値の長さを新しい基準にする */
  const startQuestion = useCallback(() => {
    clearTimer();
    judgedRef.current = false;
    shrinkRef.current = null;
    setBaselineTo(valueRef.current.length);
  }, [clearTimer, setBaselineTo]);

  return {
    answer: value.slice(baseline),
    inputProps: {
      value,
      onChange,
      onCompositionEnd,
      onKeyDown,
    },
    startQuestion,
  };
};
